import React, { MouseEventHandler, useContext } from 'react';
import classes from './AccordionTitle.module.scss';
import { AccordionContext } from '../AccordionProvider';
import { Icon } from '../../../images/Icon/Icon';
import classNames from 'classnames';
import { BreakpointsValuesEnum } from '../../../../constants/breakpoints-values';
import { useBreakpoints } from '../../../../hooks/useBreakpoints';
import { ContainerListItem } from '../../../actions/ListItem/ListItem.view';
import { MandatoryChildren } from '../../../../models/children-props';
import { ErrorMessageSeverity } from '../../../../constants/form';
import { POST_EDITOR_ERROR_CLASS } from '../../../../constants/styleConstants';

export type AccordionTitleViewProps = MandatoryChildren<{
  //
  disabled?: boolean;
  id: string;
  isFirst?: boolean;
  actionsMenu?: ContainerListItem[];
  tabindex: number;
}>;

export const AccordionTitleView = (props: AccordionTitleViewProps): JSX.Element => {
  const { expanded, setExpanded, error, iconExpanded, iconCollapsed, collapseAll, onTabChange, segmentedTitle } =
    useContext(AccordionContext);
  const breakpoint: keyof typeof BreakpointsValuesEnum = useBreakpoints();
  const hasError = error.flatMap(e => (e.severity === ErrorMessageSeverity.error ? e.id : [])).includes(props.id);
  const hasWarning = error.flatMap(e => (e.severity === ErrorMessageSeverity.warning ? e.id : [])).includes(props.id);
  const handleClick: MouseEventHandler<HTMLDivElement> = (e): void => {
    if (e.nativeEvent.target) {
      const target = e.nativeEvent.target as HTMLElement;
      if (target.onclick !== null) {
        // we have a child element that has been clicked and that child has onClick handler attached.
        // Due to that prevent onTabChange and leave that onClick event to be handled
        return;
      }
    }
    const id = e.currentTarget.id;
    const index = e.currentTarget.tabIndex;
    if (props.disabled) return;
    if (onTabChange && expanded !== id) {
      onTabChange(index);
      return;
    }
    if (expanded !== id) {
      setExpanded(id);
    } else if (onTabChange && collapseAll) {
      onTabChange(null);
      return;
    } else if (collapseAll) {
      setExpanded(null);
    }
  };

  const globalClasses = classNames(classes.AccordionTitle, {
    [classes.AccordionTitleFirst]: !!props.isFirst,
    [classes.AccordionTitleExpanded]: expanded === props.id,
    [classes.AccordionTitleDesktop]: breakpoint !== 'small' && breakpoint !== 'medium',
    [classes.AccordionTitleDesktopExpanded]: breakpoint !== 'small' && breakpoint !== 'medium' && expanded === props.id,
    [classes.AccordionTitleError]: hasError,
    [classes.AccordionTitleDesktopError]: hasError && breakpoint !== 'small' && breakpoint !== 'medium',
    [classes.AccordionTitleWarning]: !hasError && hasWarning,
    [classes.AccordionTitleDesktopWarning]:
      !hasError && hasWarning && breakpoint !== 'small' && breakpoint !== 'medium',
    [classes.AccordionTitleDisabled]: props.disabled
  });

  const icon = expanded !== props.id ? iconCollapsed : iconExpanded;
  const iconWrapperClasses = classNames(
    classes.AccordionTitleWrapper,
    'align-self-stretch',
    'grid-y',
    'align-center',
    classes.AccordionTitleIconWrapper,
    {
      [classes.AccordionTitleSegmented]: !!segmentedTitle,
      relative: !!segmentedTitle
    }
  );
  const mainWrapperClasses = classNames('cell', 'auto', classes.AccordionTitleWrapper);

  // TODO quick fix was to disable this eslint rule. However we could use a Wrapper for aria-invalid with role group and make child element with role button with all the expanded and other elems.
  return (
    // eslint-disable-next-line jsx-a11y/role-supports-aria-props
    <div
      className={globalClasses}
      id={props.id}
      aria-disabled={(expanded === props.id && !collapseAll) || props.disabled}
      aria-expanded={expanded === props.id}
      aria-controls={props.id}
      tabIndex={props.tabindex}
      onClick={handleClick}
      aria-invalid={hasError || hasWarning || undefined}
      role={'button'}>
      <div
        className={classNames(
          'grid-x',
          'align-justify',
          'align-middle',
          hasError ? POST_EDITOR_ERROR_CLASS : undefined
        )}>
        <div className={mainWrapperClasses}>{props.children}</div>
        <div className={iconWrapperClasses}>
          <div className={classes.IconWrapper}>
            <Icon name={icon} size={'medium'} />
          </div>
        </div>
      </div>
    </div>
  );
};
